import { isSupplierInventoryConfigEnabled } from '../components/common/SupplierInventoryConfigPermissionChecker';
import {
	CLOSED_PR_LINE_ITEM_STATUSES,
	PURCHASE_REQUEST_STATUSES,
} from '../constants/PurchaseRequestStatuses';
import { PURCHASE_ORDER_STATUSES } from '../constants/PurchaseOrderStatuses';
import { CONFIG_NAMES } from './AuthUtils';
import { nullSafeGet, nullSafeGetOrElse } from './DataAccessUtils';
import { FILTER_FIELD_TYPE, FILTER_VALUE_TYPE } from './DataConstants';
import {
	ALL_EPSL_CONDITIONS,
	ALL_EPSL_DISPOSITION_PLANS,
	EPSL_CONDITION_DISPLAY_NAMES,
	EPSL_DISPOSITION_PLAN_DISPLAY_NAMES,
} from './stockLocationUtils';
import posthog from 'posthog-js';

export const getPOLineItemUnitCostValue = (record, type) => {
	const quantity = nullSafeGetOrElse(`${type}Quantity`, record, 1);
	const receivedQuantity = nullSafeGetOrElse(`${type}ReceivedQuantity`, record, 1);
	const qtyToConsider = receivedQuantity > quantity ? receivedQuantity : quantity;
	const totalCost =
		record[`${type}ReceivedAverageCost`] !== undefined
			? record[`${type}ReceivedAverageCost`] * qtyToConsider
			: record[`${type}Cost`] !== undefined
			? record[`${type}Cost`]
			: undefined;
	return {
		totalCost,
		unitCost: totalCost !== undefined ? totalCost / qtyToConsider : undefined,
	};
};

export const getPurchaseOrderTotalCost = (purchaseOrder) => {
	const partLineItems = nullSafeGetOrElse('partSupplierPurchaseOrderLineItems', purchaseOrder, []);
	const totalPartCost = partLineItems.reduce(
		(acc, part) => acc + parseFloat(getPOLineItemUnitCostValue(part, 'part')['totalCost'] || 0),
		0
	);

	const equipmentLineItems = nullSafeGetOrElse(
		'equipmentSupplierPurchaseOrderLineItems',
		purchaseOrder,
		[]
	);
	const totalEquipmentCost = equipmentLineItems.reduce(
		(acc, et) => acc + parseFloat(getPOLineItemUnitCostValue(et, 'equipment')['totalCost'] || 0),
		0
	);

	return (
		totalPartCost +
		totalEquipmentCost +
		parseFloat(nullSafeGetOrElse('freightTotalBeforeTax', purchaseOrder, 0)) +
		parseFloat(nullSafeGetOrElse('miscTotalBeforeTax', purchaseOrder, 0)) +
		parseFloat(nullSafeGetOrElse('tax', purchaseOrder, 0))
	);
};

export const EPSL_CONDITION_DP_FILTERS = [
	{
		label: 'Condition',
		fieldName: 'conditions',
		mode: 'multiple',
		type: FILTER_FIELD_TYPE.OW_MULTI_SELECT,
		items: ALL_EPSL_CONDITIONS.map((value) => ({
			label: EPSL_CONDITION_DISPLAY_NAMES[value],
			value,
		})),
		valueType: FILTER_VALUE_TYPE.STRING,
	},
	{
		label: 'Disposition Plan',
		fieldName: 'dispositionPlans',
		mode: 'multiple',
		type: FILTER_FIELD_TYPE.OW_MULTI_SELECT,
		items: ALL_EPSL_DISPOSITION_PLANS.map((value) => ({
			label: EPSL_DISPOSITION_PLAN_DISPLAY_NAMES[value],
			value,
		})),
		valueType: FILTER_VALUE_TYPE.STRING,
	},
];

export const isForwardingAllowedBySettings = (companySettings) => {
	const featureEnabled = posthog.isFeatureEnabled('staging_stock_location_enabled');
	const allowedBySetting = nullSafeGetOrElse(
		'inventoryConfig.allowEquipmentForwarding',
		companySettings,
		false
	);

	return featureEnabled && allowedBySetting;
};

export const isGLCodeAllowedBySettings = (companySettings) =>
	nullSafeGetOrElse('inventoryConfig.enableGLCodeForInventory', companySettings, false);

export const PO_INTEGRATION_STATUS_DISPLAY_NAMES = {
	success: 'Success',
	in_progress: 'In Progress',
	failure: 'Failure',
};

export const canMarkPROrderedOrFulfilled = (purchaseRequest, companyConfig) => {
	const allLineItems = [
		...nullSafeGetOrElse('partSupplierPurchaseRequestLineItems', purchaseRequest, []),
		...nullSafeGetOrElse('equipmentSupplierPurchaseRequestLineItems', purchaseRequest, []),
	];
	const noPOAssociationForEveryLineItem = allLineItems.every(
		(_) => !_.associatedPurchaseOrderLineItemId
	);

	const prStatusAllowed = [
		PURCHASE_REQUEST_STATUSES.approved,
		PURCHASE_REQUEST_STATUSES.ordered,
	].includes(purchaseRequest.status);

	const pOModuleEnabled = !isSupplierInventoryConfigEnabled(
		companyConfig,
		CONFIG_NAMES.DISABLE_PURCHASE_ORDERS
	);
	const prFulfillmentAllowedWithoutPOs = isSupplierInventoryConfigEnabled(
		companyConfig,
		CONFIG_NAMES.ALLOW_PR_TO_FULFILL_WITHOUT_PO
	);

	return (
		pOModuleEnabled &&
		prFulfillmentAllowedWithoutPOs &&
		prStatusAllowed &&
		noPOAssociationForEveryLineItem
	);
};

const isOpenPRLineItem = (prLineItem) =>
	!CLOSED_PR_LINE_ITEM_STATUSES.includes(nullSafeGet('status', prLineItem));

// PR line items linked to this PO that are still open, i.e. the ones closing the PO can
// also cancel.
export const getOpenPRLineItemsOnPurchaseOrder = (purchaseOrder) =>
	[
		...nullSafeGetOrElse('partSupplierPurchaseOrderLineItems', purchaseOrder, []),
		...nullSafeGetOrElse('equipmentSupplierPurchaseOrderLineItems', purchaseOrder, []),
	]
		.flatMap((poLineItem) => nullSafeGetOrElse('supplierPurchaseRequestLineItems', poLineItem, []))
		.filter(isOpenPRLineItem);

// Mirrors the backend rule for PATCH purchase_request_line_items/cancel: an approved line
// that is not on a PO, or an open line whose PO is closed or cancelled.
export const canCancelPRLineItem = (prLineItem) => {
	if (!prLineItem || prLineItem.isDeleted) {
		return false;
	}
	const purchaseOrderStatus = nullSafeGet(
		'associatedPurchaseOrderLineItem.supplierPurchaseOrder.status',
		prLineItem
	);
	if (!nullSafeGet('associatedPurchaseOrderLineItem', prLineItem)) {
		return prLineItem.status === PURCHASE_REQUEST_STATUSES.approved;
	}
	return (
		[PURCHASE_ORDER_STATUSES.closed, PURCHASE_ORDER_STATUSES.cancelled].includes(
			purchaseOrderStatus
		) &&
		prLineItem.status !== PURCHASE_REQUEST_STATUSES.requested &&
		isOpenPRLineItem(prLineItem)
	);
};
